import { useState, useEffect, useCallback } from 'react';
import productSyncService from '~/services/productSyncService';

/**
 * Hook for using local product database (IndexedDB)
 */
export function useLocalProducts()
{
    const [isReady, setIsReady] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncStatus, setSyncStatus] = useState(null);

    // Initialize and get sync status
    useEffect(() => {
        (async () => {
            const status = await productSyncService.getSyncStatus();
            setSyncStatus(status);
            setIsReady(true);
        })();
    }, []);

    // Search products
    const searchProducts = useCallback(async (keyword, limit = 50) => {
        if (!isReady) return [];
        return await productSyncService.searchProducts(keyword, limit);
    }, [isReady]);

    // Get all products
    const getAllProducts = useCallback(async () => {
        if (!isReady) return [];
        return await productSyncService.getAllProducts();
    }, [isReady]);

    // Get product by ID
    const getProduct = useCallback(async (id) => {
        if (!isReady) return null;
        return await productSyncService.getProduct(id);
    }, [isReady]);

    // Sync products
    const syncProducts = useCallback(async () => {
        setIsSyncing(true);
        const result = await productSyncService.syncProducts();
        setIsSyncing(false);

        // Update sync status
        const status = await productSyncService.getSyncStatus();
        setSyncStatus(status);

        return result;
    }, []);

    // Force full sync
    const forceFullSync = useCallback(async () => {
        setIsSyncing(true);
        const result = await productSyncService.forceFullSync();
        setIsSyncing(false);

        // Update sync status
        const status = await productSyncService.getSyncStatus();
        setSyncStatus(status);

        return result;
    }, []);

    return {
        isReady,
        isSyncing,
        syncStatus,
        searchProducts,
        getAllProducts,
        getProduct,
        syncProducts,
        forceFullSync,
    };
}
